// frontend/src/components/Project/TaskDetailModal.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Chip,
  Button,
  Avatar,
  Alert,
  CircularProgress,
  Badge,
  Divider,
  Grid,
  Tooltip,
  Skeleton,
  alpha,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CommentIcon from '@mui/icons-material/Comment';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';

import { tasksAPI } from '../../services/api';
import TaskCommentsChat from './TaskCommentsChat';

// 🔥 ИМПОРТ КОНСТАНТ
import {
  TASK_STATUS,
  TASK_STATUS_LABELS,
  PRIORITY,
  PRIORITY_LABELS,
  getTaskStatusText,
  getPriorityText,
  getTaskStatusMuiColor,
  getPriorityMuiColor
} from '../../constants/taskConstants';

// ============================================================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ С ГРАДИЕНТОМ
// ============================================================================

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(4),
    maxWidth: 760,
    minWidth: 0,
    width: 'min(760px, calc(100% - 24px))',
    height: '85vh',
    maxHeight: '900px',
    overflow: 'hidden',
    zIndex: 1300,
    border: `1px solid ${alpha(theme.palette.common.white, 0.9)}`,
    background: `linear-gradient(145deg,
      ${alpha(theme.palette.common.white, 0.68)},
      ${alpha(theme.palette.grey[100], 0.42)} 46%,
      ${alpha(theme.palette.primary.main, 0.12)}),
      radial-gradient(circle at 12% 8%, ${alpha(theme.palette.info.light, 0.22)}, transparent 38%),
      radial-gradient(circle at 88% 82%, ${alpha(theme.palette.primary.main, 0.20)}, transparent 44%)`,
    backgroundColor: alpha(theme.palette.common.white, 0.48),
    backdropFilter: 'blur(28px) saturate(145%)',
    WebkitBackdropFilter: 'blur(28px) saturate(145%)',
    boxShadow: `0 30px 100px ${alpha(theme.palette.common.black, 0.24)}`,
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      borderRadius: theme.spacing(3),
      width: 'calc(100% - 24px)',
      height: 'calc(100dvh - 24px)',
      maxHeight: 'none'
    }
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.grey[900], 0.28)
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2.2, 2.5),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flexShrink: 0,
  color: theme.palette.text.primary,
  background: `linear-gradient(135deg, ${alpha(theme.palette.common.white, 0.56)}, ${alpha(theme.palette.common.white, 0.18)})`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.72)}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.6, 1.8)
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.common.white, 0.16),
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.62)}`,
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '999px 999px 0 0',
    backgroundColor: theme.palette.primary.main
  },
  '& .MuiTab-root': {
    color: alpha(theme.palette.text.primary, 0.68),
    fontWeight: 650,
    fontSize: '0.92rem',
    textTransform: 'none',
    minHeight: 58,
    transition: 'all 160ms ease',
    '&.Mui-selected': {
      color: theme.palette.primary.main
    },
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.common.white, 0.28)
    }
  }
}));

const StyledTabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  overflow: 'auto',
  color: theme.palette.text.primary,
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.common.white, 0.36),
    borderRadius: '999px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.secondary, 0.28),
    borderRadius: '999px',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.55),
    },
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2)
  }
}));

// ============================================================================
// 🔥 КОМПОНЕНТ СКЕЛЕТОНА
// ============================================================================

const TaskSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    {/* Заголовок */}
    <Box>
      <Skeleton variant="text" width="80%" height={40} sx={{ bgcolor: alpha('#000', 0.08) }} />
      <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1, bgcolor: alpha('#000', 0.06) }} />
      <Divider sx={{ mt: 2 }} />
    </Box>

    {/* Информационные поля */}
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Skeleton variant="text" width="50%" height={16} sx={{ bgcolor: alpha('#000', 0.06) }} />
        <Skeleton variant="text" width="80%" height={28} sx={{ mt: 0.5, bgcolor: alpha('#000', 0.08) }} />
      </Grid>
      <Grid item xs={6}>
        <Skeleton variant="text" width="50%" height={16} sx={{ bgcolor: alpha('#000', 0.06) }} />
        <Skeleton variant="text" width="80%" height={28} sx={{ mt: 0.5, bgcolor: alpha('#000', 0.08) }} />
      </Grid>
      <Grid item xs={12}>
        <Skeleton variant="text" width="40%" height={16} sx={{ bgcolor: alpha('#000', 0.06) }} />
        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
          <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: alpha('#000', 0.08) }} />
          <Skeleton variant="text" width="60%" height={28} sx={{ bgcolor: alpha('#000', 0.08) }} />
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Skeleton variant="text" width="50%" height={16} sx={{ bgcolor: alpha('#000', 0.06) }} />
        <Skeleton variant="text" width="70%" height={28} sx={{ mt: 0.5, bgcolor: alpha('#000', 0.08) }} />
      </Grid>
    </Grid>

    {/* Описание */}
    <Box>
      <Skeleton variant="text" width="30%" height={16} sx={{ bgcolor: alpha('#000', 0.06) }} />
      <Skeleton variant="text" width="100%" height={20} sx={{ mt: 1, bgcolor: alpha('#000', 0.06) }} />
      <Skeleton variant="text" width="95%" height={20} sx={{ bgcolor: alpha('#000', 0.06) }} />
      <Skeleton variant="text" width="80%" height={20} sx={{ bgcolor: alpha('#000', 0.06) }} />
    </Box>
  </Box>
);

// ============================================================================
// КОМПОНЕНТ ВКЛАДКИ
// ============================================================================

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`task-tabpanel-${index}`}
      aria-labelledby={`task-tab-${index}`}
      style={{ height: '100%' }}
      {...other}
    >
      {value === index && (
        <StyledTabPanel>
          {children}
        </StyledTabPanel>
      )}
    </div>
  );
}

// ============================================================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================================================

const TaskDetailModal = ({ open, onClose, taskId, onTaskUpdated }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [task, setTask] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (open && taskId) {
      fetchTaskDetails();
    } else {
      setTask(null);
      setError(null);
      setSuccess(null);
      setTabValue(0);
    }
  }, [open, taskId]);

  const fetchTaskDetails = async () => {
    if (!taskId) return;

    setIsLoading(true);
    setError(null);

    try {
      const taskData = await tasksAPI.getTask(taskId);
      setTask(taskData);
    } catch (error) {
      console.error('❌ Ошибка загрузки задачи:', error);
      setError(error.message || 'Не удалось загрузить данные задачи');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleQuickStatusChange = async (newStatus) => {
    if (!task) return;

    try {
      await tasksAPI.updateTaskStatus(task.id, newStatus);

      setTask(prev => ({ ...prev, status: newStatus }));

      if (onTaskUpdated) {
        onTaskUpdated();
      }

      setSuccess(`Статус изменен на "${getTaskStatusText(newStatus)}"`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      console.error('❌ Ошибка изменения статуса:', error);
      setError(error.message || 'Не удалось изменить статус задачи');
    }
  };

  const handleDeleteTask = async () => {
    if (!task) return;

    if (!window.confirm('Вы уверены, что хотите удалить эту задачу? Это действие нельзя отменить.')) {
      return;
    }

    try {
      await tasksAPI.deleteTask(task.id);

      if (onTaskUpdated) {
        onTaskUpdated();
      }

      onClose();
    } catch (error) {
      console.error('❌ Ошибка удаления задачи:', error);
      setError(error.message || 'Не удалось удалить задачу');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указан';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (e) {
      return 'Некорректная дата';
    }
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === TASK_STATUS.DONE) return false;
    try {
      const due = new Date(dueDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      due.setHours(0, 0, 0, 0);
      return due < today;
    } catch (e) {
      return false;
    }
  };

  const getUserInitials = (user) => {
    if (!user) return '?';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`;
    return initials.trim() || user.email?.charAt(0)?.toUpperCase() || '?';
  };

  // Показываем скелетон во время загрузки
  const showSkeleton = isLoading && !task;

  // 🔥 Вычисляем overdue до проверки !task
  const overdue = task ? isOverdue(task.due_date, task.status) : false;

  if (!task && !isLoading) return null;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      TransitionComponent={undefined}
      transitionDuration={0}
      keepMounted={false}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AssignmentIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" fontWeight="700">
            {showSkeleton ? 'Загрузка...' : task?.title || 'Детали задачи'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!showSkeleton && task?.status !== TASK_STATUS.DONE && (
            <Button
              variant="contained"
              size="small"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleQuickStatusChange(TASK_STATUS.DONE)}
              sx={{
                minHeight: 40,
                px: 2,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 700,
                color: theme.palette.success.dark,
                bgcolor: alpha(theme.palette.success.main, 0.14),
                border: `1px solid ${alpha(theme.palette.success.main, 0.28)}`,
                boxShadow: `0 6px 18px ${alpha(theme.palette.success.main, 0.12)}`,
                '&:hover': {
                  bgcolor: alpha(theme.palette.success.main, 0.22),
                  boxShadow: `0 8px 22px ${alpha(theme.palette.success.main, 0.16)}`
                }
              }}
            >
              Выполнено
            </Button>
          )}
          <IconButton
            onClick={handleDeleteTask}
            disabled={showSkeleton}
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              color: alpha(theme.palette.text.primary, 0.72),
              backgroundColor: alpha(theme.palette.common.white, 0.56),
              border: `1px solid ${alpha(theme.palette.common.white, 0.9)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.06)}`,
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.white, 0.8)
              }
            }}
          >
            <DeleteIcon />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              color: alpha(theme.palette.text.primary, 0.72),
              backgroundColor: alpha(theme.palette.common.white, 0.56),
              border: `1px solid ${alpha(theme.palette.common.white, 0.9)}`,
              boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.06)}`,
              '&:hover': {
                backgroundColor: alpha(theme.palette.common.white, 0.8)
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </StyledDialogTitle>

      <Box sx={{ position: 'relative' }}>
        <Box sx={{ borderBottom: 1, borderColor: alpha(theme.palette.common.white, 0.62) }}>
          <StyledTabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
            <Tab icon={<InfoIcon />} iconPosition="start" label="Информация" />
            <Tab
              icon={<CommentIcon />}
              iconPosition="start"
              label={
                <Badge
                  badgeContent={null}
                  color="primary"
                  variant="dot"
                  invisible={tabValue !== 1}
                  sx={{ '& .MuiBadge-badge': { bgcolor: theme.palette.primary.main } }}
                >
                  Комментарии
                </Badge>
              }
            />
          </StyledTabs>
        </Box>

        {(error || success) && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            p: 2,
            pointerEvents: 'none',
          }}>
            {error && (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2.5,
                  pointerEvents: 'auto',
                  boxShadow: `0 12px 36px ${alpha(theme.palette.common.black, 0.12)}`,
                  backdropFilter: 'blur(16px)',
                  backgroundColor: alpha(theme.palette.common.white, 0.72)
                }}
                onClose={() => setError(null)}
              >
                {error}
              </Alert>
            )}

            {success && (
              <Alert
                severity="success"
                sx={{
                  borderRadius: 2.5,
                  pointerEvents: 'auto',
                  boxShadow: `0 12px 36px ${alpha(theme.palette.common.black, 0.12)}`,
                  backdropFilter: 'blur(16px)',
                  backgroundColor: alpha(theme.palette.common.white, 0.72)
                }}
                onClose={() => setSuccess(null)}
              >
                {success}
              </Alert>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <TabPanel value={tabValue} index={0}>
          {showSkeleton ? (
            <TaskSkeleton />
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: 2.5, backdropFilter: 'blur(16px)', backgroundColor: alpha(theme.palette.common.white, 0.68) }}>
              {error}
            </Alert>
          ) : task ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="h4" fontWeight="300" color="primary.main">
                  {task.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Создана: {formatDate(task.created_at)}
                </Typography>
                <Divider sx={{ mt: 1, borderColor: alpha(theme.palette.common.white, 0.62) }} />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Статус
                  </Typography>
                  <Typography variant="body2" fontWeight="500" sx={{ mt: 0.5, color: theme.palette.text.primary }}>
                    {getTaskStatusText(task.status)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Приоритет
                  </Typography>
                  <Typography variant="body2" fontWeight="500" sx={{ mt: 0.5, color: theme.palette.text.primary }}>
                    {getPriorityText(task.priority)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Исполнители
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                    {task.assignees && task.assignees.length > 0 ? (
                      task.assignees.map((assignee) => (
                        <Box key={assignee.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title={`${assignee.first_name || ''} ${assignee.last_name || ''}`.trim() || assignee.email}>
                            <Avatar sx={{ width: 30, height: 30, bgcolor: alpha(theme.palette.primary.main, 0.88), fontSize: '0.75rem', boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.18)}` }}>
                              {getUserInitials(assignee)}
                            </Avatar>
                          </Tooltip>
                          <Typography variant="body2" fontWeight="500" sx={{ color: theme.palette.text.primary }}>
                            {`${assignee.first_name || ''} ${assignee.last_name || ''}`.trim() || assignee.email}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" fontWeight="500" sx={{ color: theme.palette.common.white }}>
                        Не назначены
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Срок выполнения
                  </Typography>
                  <Typography variant="body2" fontWeight="500" color={overdue ? 'error' : 'text.primary'} sx={{ mt: 0.5 }}>
                    {task.due_date ? formatDate(task.due_date) : 'Не указан'}
                    {overdue && ' (просрочено)'}
                  </Typography>
                </Grid>
              </Grid>

              {task.description && (
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }} gutterBottom>
                    Описание
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontWeight: 400, color: theme.palette.text.primary, lineHeight: 1.7 }}>
                    {task.description}
                  </Typography>
                </Box>
              )}

              {task.tags?.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }} gutterBottom>
                    Теги
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {task.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        sx={{
                          height: 28,
                          borderRadius: 999,
                          backgroundColor: alpha(theme.palette.common.white, 0.46),
                          border: `1px solid ${alpha(theme.palette.common.white, 0.78)}`,
                          color: theme.palette.text.primary,
                          fontWeight: 600,
                          '& .MuiChip-label': { color: theme.palette.text.primary, px: 1.3 }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ height: '100%' }}>
            {showSkeleton ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : (
              <TaskCommentsChat taskId={task?.id} />
            )}
          </Box>
        </TabPanel>
      </Box>
    </StyledDialog>
  );
};

export default TaskDetailModal;